import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSocialCoachSession } from "./useSocialCoachSession";
import { PlayersServices, type PlayerFilters } from "@/services/bedrock";
import { useCallback, useMemo } from "react";

function buildPlayersQueryOptions(
  coachId: number | undefined,
  params: PlayerFilters = {},
  options?: { signal?: AbortSignal }
) {
  const extraFieldsKey = Array.isArray(params.extraFields) ? params.extraFields.slice().sort() : [];
  return {
    queryKey: [
      "coachPlayersList",
      coachId,
      params.search,
      params.page,
      params.perPage,
      params.sort,
      params.sortDirection,
      params.segmentation,
      params.playerIds,
      params.hasPlayerCard,
      extraFieldsKey,
    ] as const,
    queryFn: () => PlayersServices.getPlayers(params, options),
    staleTime: 1000 * 60 * 5, // 5 minutes
  };
}

export const usePlayerFetch = () => {
  const { coachId } = useSocialCoachSession();
  const queryClient = useQueryClient();

  const fetchPlayers = useCallback(
    (params: PlayerFilters = {}, options?: { signal?: AbortSignal }) =>
      queryClient.fetchQuery(buildPlayersQueryOptions(coachId, params, options)),
    [coachId, queryClient]
  );

  const clearPlayerFetchCache = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["coachPlayersList", coachId] }).catch(console.error);
  }, [coachId, queryClient]);

  return useMemo(
    () => ({
      fetchPlayers,
      clearPlayerFetchCache,
    }),
    [fetchPlayers, clearPlayerFetchCache]
  );
};

const useSearchPlayers = (params: PlayerFilters = {}) => {
  const { coachId } = useSocialCoachSession();
  const options = useMemo(() => buildPlayersQueryOptions(coachId, params), [coachId, params]);
  const { data: players, isFetching } = useQuery({
    ...options,
    placeholderData: keepPreviousData,
  });
  return { players, isFetching };
};
